import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { getAllDashboards, deleteDashboard, clearAllDashboards } from '../utils/db';
import { notificationManager } from '../utils/NotificationManager';
import { getToken } from 'firebase/messaging';
import { messaging } from '../firebase';
import '../styles/Global.css';
import '../styles/Settings.css';
import Sun from '../icons/Sun';
import Moon from '../icons/Moon';

const Settings = ({ user }) => {
  const { theme, toggleTheme } = useTheme();
  const { t, i18n } = useTranslation();
  const isDarkMode = theme === 'dark';
  const [cachedDashboards, setCachedDashboards] = useState([]);
  const [notificationsActive, setNotificationsActive] = useState(false);
  const [sessionStatus, setSessionStatus] = useState('inactive'); // 'active', 'inactive', 'conflict'
  const [isProcessing, setIsProcessing] = useState(false);
  const [swVersion, setSwVersion] = useState('Checking...');

  useEffect(() => {
    const initSettings = async () => {
      try {
        const dashboards = await getAllDashboards();
        setCachedDashboards(dashboards);

        if (user) {
          const status = await notificationManager.checkSessionConflict(user.uid);
          setSessionStatus(status || 'inactive');
          setNotificationsActive(status === 'active');
        }

        // Get Service Worker version dynamically
        if ('serviceWorker' in navigator) {
          const registration = await navigator.serviceWorker.getRegistration();
          if (registration && registration.active) {
            try {
              const messageChannel = new MessageChannel();
              messageChannel.port1.onmessage = (event) => {
                if (event.data && event.data.version) {
                  setSwVersion(`v${event.data.version}`);
                }
              };
              registration.active.postMessage({ type: 'GET_VERSION' }, [messageChannel.port2]);

              // Timeout fallback
              setTimeout(() => {
                if (swVersion === 'Checking...') {
                  setSwVersion('Unknown');
                }
              }, 2000);
            } catch (error) {
              console.error('Error getting SW version:', error);
              setSwVersion('Error');
            }
          } else {
            setSwVersion('Not Active');
          }
        } else {
          setSwVersion('Not Supported');
        }
      } catch (error) {
        console.error("Error initializing settings:", error);
      }
    };
    initSettings();
  }, [user]);

  const handleToggleNotifications = async () => {
    setIsProcessing(true);
    try {
      if (notificationsActive) {
        // Unregister
        const token = await getToken(messaging);
        if (token) await notificationManager.unregisterToken(token);
        setNotificationsActive(false);
        setSessionStatus('inactive');
      } else {
        // Register
        const token = await notificationManager.registerToken();
        if (token) {
          setNotificationsActive(true);
          setSessionStatus('active');
        } else {
          // If registerToken returns null, it might be due to permission or VAPID issue
          throw new Error(t('errorNotificationActivation', 'No se pudo activar las notificaciones. Verifica los permisos del navegador o la clave VAPID.'));
        }
      }
    } catch (e) {
      console.error("Error toggling notifications:", e);
      alert(`${t('errorNotificationGeneric', 'Error con las notificaciones:')} ${e.message}`);
      // Revert status if needed
      if (user) {
        const status = await notificationManager.checkSessionConflict(user.uid);
        setSessionStatus(status || 'inactive');
        setNotificationsActive(status === 'active');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const handleDelete = async (id) => {
    try {
      await deleteDashboard(id);
      setCachedDashboards(cachedDashboards.filter(d => d.id !== id));
    } catch (error) {
      console.error(`Error deleting dashboard ${id}:`, error);
    }
  };

  const handleClearAll = async () => {
    try {
      await clearAllDashboards();
      setCachedDashboards([]);
    } catch (error) {
      console.error("Error clearing all dashboards:", error);
    }
  };

  return (
    <div className="settings-page-container">
      <h1 className="page-title">{t('settingsPageTitle')}</h1>

      <section className="settings-section">
        <h2 className="settings-section-title">{t('settingsAppearanceTitle')}</h2>
        <div className="setting-item">
          <span className="setting-label">
            {t('settingsThemeLabel')} {isDarkMode ? t('darkMode') : t('lightMode')}
          </span>
          <label htmlFor="theme-switch-checkbox" className="theme-switch-container" aria-label={isDarkMode ? t('settingsSwitchToLightButton') : t('settingsSwitchToDarkButton')}>
            <input
              type="checkbox"
              id="theme-switch-checkbox"
              className="theme-checkbox"
              checked={isDarkMode}
              onChange={toggleTheme}
            />
            <span className="theme-switch-track">
              <span className="theme-switch-thumb">
                {isDarkMode ? <Moon className="theme-icon" /> : <Sun className="theme-icon" />}
              </span>
            </span>
          </label>
        </div>
      </section>

      <section className="settings-section">
        <h2 className="settings-section-title">{t('settingsLanguageTitle')}</h2>
        <div className="setting-item">
          <span className="setting-label">{t('settingsSelectLanguageLabel')}</span>
          <div className="language-selector-buttons">
            <button
              onClick={() => changeLanguage('en')}
              className={`language-button ${i18n.resolvedLanguage === 'en' ? 'active' : ''}`}
              disabled={i18n.resolvedLanguage === 'en'}
            >
              {t('englishLanguage')}
            </button>
            <button
              onClick={() => changeLanguage('es')}
              className={`language-button ${i18n.resolvedLanguage === 'es' ? 'active' : ''}`}
              disabled={i18n.resolvedLanguage === 'es'}
            >
              {t('spanishLanguage')}
            </button>
          </div>
        </div>
      </section>

      {/* Cache Management Section */}
      <section className="settings-section">
        <h2 className="settings-section-title">{t('settingsDashboardsTitle', 'Dashboards Management')}</h2>
        <div className="setting-item-vertical">
          {cachedDashboards.length > 0 ? (
            <>
              <ul className="cache-list">
                {cachedDashboards.map(dashboard => (
                  <li key={dashboard.id} className="cache-item">
                    <div className="cache-item-info">
                      <span className="cache-item-name">{dashboard.name}</span>
                      <span className="cache-item-version">v{dashboard.version}</span>
                    </div>
                    <button onClick={() => handleDelete(dashboard.id)} className="button-danger-outline">
                      {t('deleteButton', 'Delete')}
                    </button>
                  </li>
                ))}
              </ul>
              <button onClick={handleClearAll} className="button-danger-solid clear-all-button">
                {t('clearAllDashboardsButton', 'Clear All Dashboards')}
              </button>
            </>
          ) : (
            <p className="cache-empty-message">{t('cacheEmptyMessage', 'Dashboard cache is empty.')}</p>
          )}
        </div>
      </section>

      {/* Notifications Section */}
      <section className="settings-section">
        <h2 className="settings-section-title">{t('settingsNotificationsTitle')}</h2>
        <div className="setting-item">
          <span className="setting-label">{t('settingsEnableNotificationsLabel')}</span>
          <label className={`theme-switch-container ${isProcessing ? 'disabled' : ''}`}>
            <input
              type="checkbox"
              className="theme-checkbox"
              checked={notificationsActive}
              onChange={handleToggleNotifications}
              disabled={isProcessing}
            />
            <span className="theme-switch-track">
              <span className="theme-switch-thumb"></span>
            </span>
          </label>
        </div>
        <div className="setting-item-info">
          <span className="setting-label-small">
            {isProcessing ? t('processing', 'Procesando...') : t('settingsDeviceStatusLabel')}
          </span>
          {!isProcessing && (
            <span className={`status-badge ${sessionStatus}`}>
              {t(`status${sessionStatus.charAt(0).toUpperCase() + sessionStatus.slice(1)}`)}
            </span>
          )}
        </div>
      </section>

      {/* System Version Section */}
      <section className="settings-section">
        <h2 className="settings-section-title">System Version</h2>
        <div className="setting-item-vertical">
          <div className="version-info-container">
            <div className="version-item">
              <span className="version-label">NotificationManager:</span>
              <span className="version-value">v1.0.13</span>
            </div>
            <div className="version-item">
              <span className="version-label">Service Worker:</span>
              <span className="version-value">{swVersion}</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Settings;
